import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardTitle,
  Table,
  Button,
  Spinner,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
  getMyLeaveHistory,
  cancelLeave,
} from "../../Api/ApiUrlConst";
import { LeaveHistoryDto } from "../../services/leaveModels";

const LeaveHistory: React.FC = () => {
  const [leaves, setLeaves] = useState<LeaveHistoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonText, setReasonText] = useState("");

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await getMyLeaveHistory();
      setLeaves(res.data);
    } catch (err) {
      setError("Failed to load leave history.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (leaveId: number) => {
    if (!window.confirm("Are you sure you want to cancel this leave?")) return;

    try {
      await cancelLeave(leaveId);
      loadHistory(); // refresh list
    } catch (err: any) {
      alert(err.response?.data || "Failed to cancel leave.");
    }
  };

  const canCancel = (fromDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return new Date(fromDate) >= today;
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" />
      </div>
    );
  }

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      weekday: "short",
    });

  return (
    <div className="container mt-4">
      <Card>
        <CardBody>
          <CardTitle tag="h4" className="mb-3">
            Leave History
          </CardTitle>

          {error && <Alert color="danger">{error}</Alert>}

          <Table bordered responsive hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Applied On</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {leaves.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center">
                    No leave records found.
                  </td>
                </tr>
              )}

              {leaves.map((leave, index) => (
                <tr key={leave.leaveId}>
                  <td>{index + 1}</td>
                  <td>
                    {leave.leaveTypeName} ({leave.leaveTypeCode})
                  </td>
                  <td>{formatDate(leave.fromDate)}</td>
                  <td>{formatDate(leave.toDate)}</td>
                  <td>{leave.totalDays}</td>
                  <td>

                    {leave.reason &&
                      leave.reason.trim().split(/\s+/).length > 2 ? (

                      <>
                        {leave.reason
                          .split(/\s+/)
                          .slice(0, 2)
                          .join(" ")}
                        ...

                        <Button
                          size="sm"
                          color="link"
                          onClick={() => {
                            setReasonText(leave.reason);
                            setReasonModalOpen(true);
                          }}
                        >
                          More...
                        </Button>
                      </>

                    ) : (
                      leave.reason
                    )}

                  </td>
                  <td>
                    {formatDate(leave.appliedOn)}
                  </td>
                  <td>
                    {canCancel(leave.fromDate) ? (
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => handleCancel(leave.leaveId)}
                      >
                        Cancel
                      </Button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Modal
            isOpen={reasonModalOpen}
            toggle={() => setReasonModalOpen(false)}
          >

            <ModalHeader toggle={() => setReasonModalOpen(false)}>
              Leave Reason
            </ModalHeader>

            <ModalBody>
              <Input
                type="textarea"
                rows={6}
                value={reasonText}
                readOnly
              />
            </ModalBody>

          </Modal>


        </CardBody>
      </Card>
    </div>
  );
};

export default LeaveHistory;
